/**
 * Okamžitá kontrola měření při psaní (Cockpit).
 *
 * Zaměstnanec hned vidí, jestli hodnota sedí: minulou hodnotu, limit, u hodnot,
 * které stále rostou (měřidla) nebo klesají (zásoba), přírůstek proti běžné
 * denní změně a návrh opravy překlepu (chybějící čárka). Chování hodnoty
 * nastavuje měřicí bod (`charakter`: rozmezi | rostouci | klesajici | volna).
 * Zrcadlí kontrolu na serveru (MereniService), ta při uložení rozhoduje;
 * tady jde o rychlou zpětnou vazbu, ze které se člověk učí.
 *
 * @returns {{stav: 'info'|'ok'|'pozor'|'chyba', text: string, navrh?: number,
 *           vis?: {druh: 'rozmezi', min, max, v} | {druh: 'zmena', od, v, bezne, max, smer}}|null}
 */
export const parseCislo = (raw) => {
    if (raw === '' || raw == null) return null;
    const v = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
    return Number.isNaN(v) ? NaN : v;
};

export const fmt = (v) => v == null ? '—' : Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: 3 });
/** Odhad (běžná změna) — stačí jedno desetinné místo. */
const fmtOdhad = (v) => Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: v >= 100 ? 0 : 1 });

const kdyText = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dnes = new Date();
    const vcera = new Date(); vcera.setDate(dnes.getDate() - 1);
    const cas = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === dnes.toDateString()) return `dnes ${cas}`;
    if (d.toDateString() === vcera.toDateString()) return `včera ${cas}`;
    return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
};

export function vyhodnotMereni(raw, item, checklistId) {
    const k = item.mereni_kontext;
    const limit = item.parametr?.aktualni_limit;
    const min = k?.limit_min ?? limit?.min_hodnota ?? item.min_hodnota ?? null;
    const max = k?.limit_max ?? limit?.max_hodnota ?? item.max_hodnota ?? null;
    const jed = k?.jednotka || item.jednotka || item.parametr?.jednotka || '';
    const charakter = k?.charakter || (min != null || max != null ? 'rozmezi' : 'volna');
    // Minulá hodnota = poslední měření, které nepatří tomuto checklistu
    const minule = (k?.posledni || []).find(p => !checklistId || p.checklist_id !== checklistId) || null;
    const minuleText = minule ? `Minule ${fmt(minule.hodnota)} ${jed} · ${kdyText(minule.kdy)}${minule.kdo ? ` · ${minule.kdo}` : ''}` : null;
    const rozsah = min != null && max != null ? `${fmt(min)} až ${fmt(max)}` : max != null ? `≤ ${fmt(max)}` : min != null ? `≥ ${fmt(min)}` : null;
    const visRozmezi = (v) => (min != null || max != null) ? { druh: 'rozmezi', min, max, v } : undefined;

    const v = parseCislo(raw);
    if (v === null) {
        const text = minuleText || (rozsah ? `Limit ${rozsah} ${jed}` : null);
        return text ? { stav: 'info', text, vis: charakter === 'rozmezi' ? visRozmezi(null) : undefined } : null;
    }
    if (Number.isNaN(v)) return { stav: 'chyba', text: 'Tohle není číslo' };

    if ((min != null && v < min) || (max != null && v > max)) {
        return { stav: 'chyba', text: `Mimo limit ${rozsah} ${jed}${minule ? ` · minule ${fmt(minule.hodnota)}` : ''}`, vis: visRozmezi(v) };
    }

    // Hodnota, která stále roste (stav měřidla) nebo klesá (zásoba)
    if ((charakter === 'rostouci' || charakter === 'klesajici') && minule) {
        const roste = charakter === 'rostouci';
        const p = minule.hodnota;
        const dnu = Math.max(1, (Date.now() - new Date(minule.kdy).getTime()) / 864e5);
        const d = k.prirustek_den;
        const maxZmena = (d != null ? Math.max(d * 5, d + 5, 5) : Math.max(Math.abs(p) * 0.05, 5)) * dnu;
        const bezne = d != null ? d * dnu : null;
        const zmena = roste ? v - p : p - v;
        const vis = { druh: 'zmena', od: p, v, bezne, max: maxZmena, smer: roste ? 1 : -1 };

        if (zmena < 0) {
            return { stav: 'chyba', text: roste
                ? `Menší než minule (${fmt(p)} ${jed}) — tahle hodnota jen roste. Zkontroluj displej.`
                : `Větší než minule (${fmt(p)} ${jed}) — tahle hodnota jen klesá.`, vis };
        }
        if (zmena > maxZmena) {
            for (const del of [10, 100, 1000, 10000, 100000, 1000000]) {
                const n = v / del;
                const z = roste ? n - p : p - n;
                if (z >= 0 && z <= maxZmena) {
                    return { stav: 'pozor', text: `O ${fmt(zmena)} ${jed} ${roste ? 'víc' : 'míň'} než minule — nechybí desetinná čárka?`, navrh: Math.round(n * 1000) / 1000, vis };
                }
            }
            return { stav: 'pozor', text: `O ${fmt(zmena)} ${jed} ${roste ? 'víc' : 'míň'} než minule${bezne != null ? ` (běžně ~${fmtOdhad(bezne)})` : ''}. Opravdu?`, vis };
        }
        const obdobi = dnu < 1.5 ? 'od minula' : `za ${Math.round(dnu)} ${Math.round(dnu) < 5 ? 'dny' : 'dní'}`;
        return { stav: 'ok', text: `${roste ? '+' : '−'}${fmt(zmena)} ${jed} ${obdobi}${bezne != null ? ` · běžně ~${fmtOdhad(bezne)}` : ''}`, vis };
    }

    if (charakter === 'rozmezi' && rozsah) {
        return { stav: 'ok', text: `V limitu ${rozsah} ${jed}${minule ? ` · minule ${fmt(minule.hodnota)}` : ''}`, vis: visRozmezi(v) };
    }
    if (minule) {
        const z = v - minule.hodnota;
        return { stav: 'info', text: `${z >= 0 ? '+' : '−'}${fmt(Math.abs(z))} ${jed} oproti minule (${fmt(minule.hodnota)})` };
    }
    return null;
}
